import { Injectable, inject } from '@angular/core';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import { AngularFireStorage } from '@angular/fire/compat/storage';
import { AngularFireAuth } from '@angular/fire/compat/auth';
import firebase from 'firebase/compat/app';
import { Observable, firstValueFrom, from } from 'rxjs';
import { filter, map, take, shareReplay, tap } from 'rxjs/operators';
import { LoggerService } from '@shared/services/logger.service';
import { RRHH_FLOTA_VEHICULOS_COLLECTION } from '../constants/firestore-collections';

export type ProcedenciaVehiculo = 'Nacional' | 'Importado';

export type EstadoVehiculo = 'activo' | 'baja';

/** Normaliza patente para comparación (mayúsculas, sin espacios). */
export function normalizarPatente(patente: string): string {
  return (patente || '').trim().toUpperCase().replace(/\s/g, '');
}

export interface VehiculoFlota {
  id?: string;
  /** Dominio/Patente. Obligatorio. Formato: AA 000 AA (2 letras, 3 números, 2 letras). */
  dominioPatente: string;
  /** Número de registro. Obligatorio. */
  numeroRegistro: string;
  chasis: string;
  motor: string;
  marca: string;
  modelo: string;
  anio: number;
  /** Tipo de vehículo (ej. Auto, Camioneta, SUV, Camión). */
  tipoVehiculo: string;
  anioFabricacion: number;
  modeloAnio: string;
  codigoAutomotor: string;
  color: string;
  tipoCarroceria: string;
  procedencia: ProcedenciaVehiculo;
  /** Combustible (ej. Diésel, Nafta). Opcional. */
  combustible?: string;
  /** Transmisión (ej. Manual, Automática). Opcional. */
  transmision?: string;
  /** URL pública de la foto del vehículo en Storage. Opcional. */
  fotoUrl?: string;
  /** Path en Storage de la foto (para reemplazo/eliminación). Opcional. */
  fotoStoragePath?: string;
  /** Estado: activo (por defecto) o baja. Si no existe, se considera activo. */
  estado?: EstadoVehiculo;
  bajaAt?: string;
  bajaMotivo?: string;
  createdAt?: string;
  updatedAt?: string;
}

@Injectable()
export class FlotaVehiculosService {
  private readonly afs = inject(AngularFirestore);
  private readonly storage = inject(AngularFireStorage);
  private readonly afAuth = inject(AngularFireAuth);
  private readonly logger = inject(LoggerService);

  /**
   * Cache de sesión del listado de vehículos: `shareReplay({ bufferSize:1, refCount:false })` —
   * primera llamada dispara la lectura; re-llamadas (página RRHH de vehículos, módulo logística,
   * `existsPatente`, `DocumentValidationService`) reciben el snapshot cacheado.
   * `invalidateListCache()` (en add/update/delete/darBaja/reactivar) lo resetea.
   * Ver 05-rrhh/docs/HOW_IT_WORKS.md §3.
   */
  private list$: Observable<VehiculoFlota[]> | null = null;

  /** Listado puntual (cache de sesión). Orden: dominioPatente asc. */
  list(): Observable<VehiculoFlota[]> {
    if (!this.list$) {
      const t0 = Date.now();
      this.logger.info('%c[VEHICULOS] 🔴 MISS → Firestore', 'color:#e53935;font-weight:bold');
      const ref = this.afs.collection(RRHH_FLOTA_VEHICULOS_COLLECTION, (r) => r.orderBy('dominioPatente', 'asc')).ref;
      this.list$ = from(ref.get()).pipe(
        take(1),
        map((snapshot) =>
          snapshot.docs.map((doc) => {
            const data = doc.data() as VehiculoFlota;
            return { ...data, id: doc.id } as VehiculoFlota;
          })
        ),
        tap((list) => this.logger.info(`%c[VEHICULOS] ✅ DONE total=${list.length} (${Date.now() - t0}ms)`, 'color:#2e7d32;font-weight:bold')),
        shareReplay({ bufferSize: 1, refCount: false })
      );
    } else {
      this.logger.info('%c[VEHICULOS] 🟢 HIT (cached)', 'color:#43a047;font-weight:bold');
    }
    return this.list$;
  }

  /** Invalida el cache de `list()`. Llamado tras add/update/delete/darBaja/reactivar. */
  invalidateListCache(): void {
    this.list$ = null;
  }

  /**
   * Indica si ya existe un vehículo con la misma patente (comparación normalizada, sin espacios y mayúsculas).
   * @param patente Dominio/patente a comprobar
   * @param excludeId ID a excluir (ej. el vehículo que se está editando)
   */
  existsPatente(patente: string, excludeId?: string): Observable<boolean> {
    const norm = normalizarPatente(patente);
    return this.list().pipe(
      take(1),
      map((list) =>
        list.some((v) => v.id !== excludeId && normalizarPatente(v.dominioPatente ?? '') === norm)
      )
    );
  }

  /**
   * Comprueba duplicados de patente, número de registro, chasis y motor en una lista ya cargada.
   * Útil para validar en el modal con una sola lectura de la colección.
   */
  checkDuplicados(
    list: VehiculoFlota[],
    patente: string,
    numeroRegistro: string,
    chasis: string,
    motor: string,
    excludeId?: string
  ): { patente: boolean; numeroRegistro: boolean; chasis: boolean; motor: boolean } {
    const normPatente = normalizarPatente(patente);
    const normNumReg = (numeroRegistro || '').trim().toLowerCase();
    const normChasis = (chasis || '').trim().toLowerCase();
    const normMotor = (motor || '').trim().toLowerCase();
    const other = (v: VehiculoFlota) => v.id !== excludeId;
    return {
      patente: list.some((v) => other(v) && normalizarPatente(v.dominioPatente ?? '') === normPatente),
      numeroRegistro: list.some((v) => other(v) && (v.numeroRegistro ?? '').trim().toLowerCase() === normNumReg),
      chasis: normChasis !== '' && list.some((v) => other(v) && (v.chasis ?? '').trim().toLowerCase() === normChasis),
      motor: normMotor !== '' && list.some((v) => other(v) && (v.motor ?? '').trim().toLowerCase() === normMotor)
    };
  }

  async add(vehiculo: Omit<VehiculoFlota, 'id' | 'estado' | 'bajaAt' | 'bajaMotivo' | 'createdAt' | 'updatedAt'>): Promise<string> {
    const user = await this.afAuth.currentUser;
    const id = this.afs.createId();
    const now = new Date().toISOString();
    await this.afs.collection(RRHH_FLOTA_VEHICULOS_COLLECTION).doc(id).set({
      ...vehiculo,
      estado: 'activo',
      actorId: user?.uid || 'auth-unavailable',
      actorEmail: user?.email || 'auth-unavailable',
      createdAt: now,
      updatedAt: now
    });
    this.invalidateListCache();
    return id;
  }

  async update(
    id: string,
    vehiculo: Partial<Omit<VehiculoFlota, 'id' | 'createdAt'>>
  ): Promise<void> {
    const user = await this.afAuth.currentUser;
    const updatedAt = new Date().toISOString();
    await this.afs.collection(RRHH_FLOTA_VEHICULOS_COLLECTION).doc(id).update({
      ...vehiculo,
      actorId: user?.uid || 'auth-unavailable',
      actorEmail: user?.email || 'auth-unavailable',
      updatedAt
    });
    this.invalidateListCache();
  }

  async delete(id: string): Promise<void> {
    await this.afs.collection(RRHH_FLOTA_VEHICULOS_COLLECTION).doc(id).delete();
    this.invalidateListCache();
  }

  /** Marca el vehículo como dado de baja (no se borra, queda con estado 'baja'). */
  async darBaja(id: string, motivo?: string): Promise<void> {
    const user = await this.afAuth.currentUser;
    const now = new Date().toISOString();
    await this.afs.collection(RRHH_FLOTA_VEHICULOS_COLLECTION).doc(id).update({
      estado: 'baja',
      bajaAt: now,
      bajaMotivo: motivo ?? null,
      actorId: user?.uid || 'auth-unavailable',
      actorEmail: user?.email || 'auth-unavailable',
      updatedAt: now
    });
    this.invalidateListCache();
  }

  /** Reactiva un vehículo que estaba dado de baja. */
  async reactivar(id: string): Promise<void> {
    const user = await this.afAuth.currentUser;
    const now = new Date().toISOString();
    await this.afs.collection(RRHH_FLOTA_VEHICULOS_COLLECTION).doc(id).update({
      estado: 'activo',
      bajaAt: null,
      bajaMotivo: null,
      actorId: user?.uid || 'auth-unavailable',
      actorEmail: user?.email || 'auth-unavailable',
      updatedAt: now
    });
    this.invalidateListCache();
  }

  /** Sube o reemplaza la foto del vehículo en Storage y devuelve URL + path persistibles. */
  async uploadFoto(vehiculoId: string, file: File): Promise<{ fotoUrl: string; fotoStoragePath: string }> {
    const ext = file.name.includes('.') ? file.name.slice(file.name.lastIndexOf('.')) : '.jpg';
    const fotoStoragePath = `rrhh/flota_vehiculos/${vehiculoId}/foto${ext}`;
    const uploadTask = this.storage.upload(fotoStoragePath, file);
    await firstValueFrom(
      uploadTask.snapshotChanges().pipe(
        filter((snapshot): snapshot is NonNullable<typeof snapshot> => snapshot != null && snapshot.state === 'success'),
        take(1)
      )
    );
    const fotoUrl = await firstValueFrom(this.storage.ref(fotoStoragePath).getDownloadURL());
    return { fotoUrl, fotoStoragePath };
  }

  /** Elimina la foto del vehículo en Storage y borra los campos en Firestore. */
  async clearFoto(vehiculoId: string, fotoStoragePath?: string): Promise<void> {
    const storagePath = (fotoStoragePath ?? '').trim();
    if (storagePath) {
      try {
        await firstValueFrom(this.storage.ref(storagePath).delete());
      } catch {
        // Si el archivo ya no existe en storage, continuar con la limpieza del registro.
      }
    }

    const user = await this.afAuth.currentUser;
    await this.afs.collection(RRHH_FLOTA_VEHICULOS_COLLECTION).doc(vehiculoId).update({
      fotoUrl: firebase.firestore.FieldValue.delete(),
      fotoStoragePath: firebase.firestore.FieldValue.delete(),
      actorId: user?.uid || 'auth-unavailable',
      actorEmail: user?.email || 'auth-unavailable',
      updatedAt: new Date().toISOString()
    });
    this.invalidateListCache();
  }
}
